const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

export class ApiClientError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

async function parseError(res: Response): Promise<ApiClientError> {
  let code = "UNKNOWN";
  let message = res.statusText || "Request failed";
  let details: unknown;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string; details?: unknown };
      code?: string;
      message?: string;
    };
    code = body.error?.code ?? body.code ?? code;
    message = body.error?.message ?? body.message ?? message;
    details = body.error?.details ?? body;
  } catch {
    /* ignore */
  }
  return new ApiClientError(res.status, code, message, details);
}

/** Per-attempt limit. Render free cold starts take 30–60 s; see authStore.refreshMe. */
export const API_TIMEOUT_MS = 25_000;
const RETRY_DELAY_MS = 1_000;
export const UNREACHABLE_MESSAGE = "Can't reach the server.";

/** Errors that mean "no answer from the API" (vs. the API answering with an error). */
const UNREACHABLE_CODES = new Set(["TIMEOUT", "NETWORK", "UPSTREAM_TIMEOUT", "UPSTREAM_UNREACHABLE", "PROXY_NOT_CONFIGURED"]);

export function isServerUnreachable(err: unknown): boolean {
  if (!(err instanceof ApiClientError)) return false;
  if (UNREACHABLE_CODES.has(err.code)) return true;
  // A gateway page (Render/Cloudflare HTML), not our JSON: the API isn't up.
  // Our own 503 UNAVAILABLE (e.g. OTP_SEND_FAILED) is an answer, not this.
  return [502, 503, 504].includes(err.status) && err.code === "UNKNOWN";
}

export interface ApiFetchOptions {
  /** Abort an attempt after this long (default 25 s). */
  timeoutMs?: number;
  /** Extra attempts when the server is unreachable. Default 1 for GET/HEAD, 0 otherwise (a POST may have landed). */
  retries?: number;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

async function apiFetchOnce<T>(path: string, init: RequestInit, headers: Headers, timeoutMs: number): Promise<T> {
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  const outer = init.signal;
  const onOuterAbort = () => controller.abort();
  outer?.addEventListener("abort", onOuterAbort);
  try {
    let res: Response;
    try {
      res = await fetch(`${API_URL}${path}`, {
        ...init,
        headers,
        credentials: "include",
        signal: controller.signal,
      });
    } catch (err) {
      if (timedOut) throw new ApiClientError(0, "TIMEOUT", `${UNREACHABLE_MESSAGE} It took too long to answer.`);
      if (outer?.aborted) throw err;
      throw new ApiClientError(0, "NETWORK", UNREACHABLE_MESSAGE);
    }
    if (res.status === 204) return undefined as T;
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as T;
  } catch (err) {
    if (timedOut && !(err instanceof ApiClientError)) {
      throw new ApiClientError(0, "TIMEOUT", `${UNREACHABLE_MESSAGE} It took too long to answer.`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
    outer?.removeEventListener("abort", onOuterAbort);
  }
}

export async function apiFetch<T>(
  path: string,
  init: RequestInit = {},
  opts: ApiFetchOptions = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? "GET").toUpperCase();
  if (method !== "GET" && method !== "HEAD") {
    headers.set("X-Requested-With", "haulsheet");
    if (init.body && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
  }
  const retries = opts.retries ?? (method === "GET" || method === "HEAD" ? 1 : 0);
  for (let attempt = 0; ; attempt++) {
    try {
      return await apiFetchOnce<T>(path, init, headers, opts.timeoutMs ?? API_TIMEOUT_MS);
    } catch (err) {
      if (attempt < retries && isServerUnreachable(err) && !init.signal?.aborted) {
        await sleep(RETRY_DELAY_MS);
        continue;
      }
      throw err;
    }
  }
}

export function apiUrl(path: string): string {
  return `${API_URL}${path}`;
}

export { API_URL };
